import React, { JSX } from 'react';
import { motion } from 'framer-motion';
import { GameState, PlayableWordInfo } from "../../WordDriftUtils";
import type { GameWord } from "../../Utils/GameTypes";
import { useSelector } from 'react-redux';
import { RootState } from '@mental-mint/shared-ui';

// Constants
const WIN_GREEN = "#22c55e"; // Green color used for win state and target word highlighting
const GRAY_COLOR = "rgba(150, 150, 150, 0.5)";
const WHITE_BACKGROUND = "rgba(255, 255, 255, 0.85)";
const MIN_WORD_LENGTH = 2; // Minimum length for a word to be clickable

// Helper function to add alpha to any color format
export const addAlphaToColor = (color: string, alpha: number): string => {
  // If it's already rgba, replace the alpha value
  if (color.startsWith('rgba')) {
    return color.replace(/,\s*[\d.]+\)$/, `, ${alpha})`);
  }
  
  // If it's rgb, convert to rgba
  if (color.startsWith('rgb(')) {
    return color.replace('rgb(', 'rgba(').replace(')', `, ${alpha})`);
  }
  
  // If it's hex, convert to rgba
  if (color.startsWith('#')) {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  
  // Fallback: just return the color
  return color;
};

interface ClickableWordProps {
  word: string;
  gameColor: string;
  onClick: () => void;
  keyId: string;
  highlightWord?: boolean;
  isGrayedOut?: boolean;
}

export const ClickableWord: React.FC<ClickableWordProps> = ({ word, gameColor, onClick, keyId, highlightWord = false, isGrayedOut = false }) => {
  const [isHovered, setIsHovered] = React.useState(false);
  
  // Define colors for normal and grayed-out states
  const normalColor = highlightWord ? WIN_GREEN : gameColor;
  
  // Calculate background colors for better visual feedback
  const getBackgroundColor = () => {
    if (isGrayedOut) {
      return addAlphaToColor(GRAY_COLOR, 0.2);
    }
    return WHITE_BACKGROUND;
  };
  
  // Calculate border width for emphasis
  const getBorderWidth = () => {
    if (highlightWord) return '2px';
    return '1.5px';
  };
  
  // Calculate shadow for depth
  const getBoxShadow = () => {
    if (isGrayedOut) return 'none';
    if (highlightWord) {
      return [
        `0 2px 8px ${addAlphaToColor(WIN_GREEN, 0.4)}`,
        `0 4px 15px ${addAlphaToColor(WIN_GREEN, 0.6)}`,
        `0 2px 8px ${addAlphaToColor(WIN_GREEN, 0.4)}`,
      ];
    }
    return `0 1px 3px ${addAlphaToColor(normalColor, 0.15)}`;
  };
  
  return (
    <motion.span
      key={keyId}
      initial={{
        color: normalColor,
        borderColor: normalColor,
        backgroundColor: addAlphaToColor(normalColor, 0.15),
        opacity: 1,
      }}
      animate={{
        color: isGrayedOut ? GRAY_COLOR : normalColor,
        borderColor: isGrayedOut ? GRAY_COLOR : normalColor,
        backgroundColor: getBackgroundColor(),
        opacity: isGrayedOut ? 0.4 : 1,
        scale: highlightWord ? [1, 1.08, 1] : 1, // Only scale for goal word animation
        boxShadow: getBoxShadow(),
      }}
      transition={{
        // Graying out animations take 1 second, green highlighting takes 1 second
        color: { duration: 1, ease: "easeInOut" },
        borderColor: { duration: 1, ease: "easeInOut" },
        backgroundColor: { duration: isGrayedOut ? 1 : 0.2, ease: "easeInOut" },
        opacity: { duration: 1, ease: "easeInOut" },
        scale: isGrayedOut ? { duration: 0 } : (highlightWord ? { duration: 2, ease: "easeInOut", repeat: Infinity } : { duration: 0.2 }),
        boxShadow: isGrayedOut ? { duration: 0 } : (highlightWord ? { duration: 2, ease: "easeInOut", repeat: Infinity } : { duration: 0.2 }),
      }}
      style={{
        fontSize: "1rem",
        cursor: isGrayedOut ? "not-allowed" : "pointer",
        textDecoration: "none",
        fontWeight: "normal",
        padding: "4px 8px",
        margin: "0 3px",
        whiteSpace: "nowrap",
        borderRadius: "6px",
        border: `${getBorderWidth()} solid`,
        display: "inline-block",
      }}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onTouchStart={() => setIsHovered(true)}
      onTouchEnd={() => setIsHovered(false)}
    >
      {word}
    </motion.span>
  );
};

interface WordDisplayProps {
  /** The word info to display */
  wordInfo: PlayableWordInfo;
  /** Current game state for styling and target word detection */
  gameState: GameState;
  /** Color to use for styling */
  gameColor: string;
  /** Callback when a word is selected */
  onWordSelection: (word: string) => void;
  /** Set of words that should be grayed out (bad moves) */
  grayedOutWords?: Set<string>;
  /** Make all words unclickable (used for goal definition display) */
  makeWordsUnclickable?: boolean;
}

export const WordDisplay: React.FC<WordDisplayProps> = ({
  wordInfo,
  gameState,
  gameColor,
  onWordSelection,
  grayedOutWords = new Set(),
  makeWordsUnclickable = false
}) => {
  const { currentGame } = useSelector((state: RootState) => state.gameSession);
  const game = currentGame!;

  // Check if the goal word is visible and clickable on the screen
  const isGoalWordVisibleAndClickable = () => {
    if (!gameState) return false;
    
    const goalWordLower = gameState.goalWord.toLowerCase();
    // Check in all clickable words if any would lead to the goal word (exact match or lemmatized match)
    return wordInfo.clickableWords.some(cleanWord => {
      const targetGameWord = wordInfo.wordLemmaMap[cleanWord];
      return targetGameWord && (
        targetGameWord.variations.display.toLowerCase() === goalWordLower || 
        targetGameWord.variations.lemmatized.toLowerCase() === goalWordLower
      );
    });
  };

  // Render "Related" words section with clickable word blobs
  const createWordBlobs = (
    words: Array<GameWord | string>, 
    keyPrefix: string,
    isGameWord: boolean = true
  ): JSX.Element[] => {
    // Deduplicate words based on their display text/string value
    const uniqueWords = words.filter((item, index, arr) => {
      const currentWord = isGameWord ? (item as GameWord).variations.display : (item as string);
      return arr.findIndex(otherItem => {
        const otherWord = isGameWord ? (otherItem as GameWord).variations.display : (otherItem as string);
        return currentWord.toLowerCase() === otherWord.toLowerCase();
      }) === index;
    });

    return uniqueWords.map((item, i) => {
      const word = isGameWord ? (item as GameWord).variations.display : (item as string);
      const cleanWord = word.toLowerCase().replace(/[^a-z0-9]/g, '');
      // If makeWordsUnclickable is true, nothing is clickable
      // Otherwise, check if word is in clickableWords array
      const isClickable = !makeWordsUnclickable && cleanWord.length > MIN_WORD_LENGTH && wordInfo.clickableWords.includes(cleanWord);
      const targetGameWord = isGameWord ? wordInfo.wordLemmaMap[cleanWord] || (item as GameWord) : wordInfo.wordLemmaMap[cleanWord];
      const goalWordLower = gameState?.goalWord.toLowerCase();
      const isTargetWord = gameState && targetGameWord && (
        targetGameWord.variations.display.toLowerCase() === goalWordLower || 
        targetGameWord.variations.lemmatized.toLowerCase() === goalWordLower
      );
      
      // Check if this word is grayed out
      const isGrayedOut = grayedOutWords.has(cleanWord) || 
        (targetGameWord && grayedOutWords.has(targetGameWord.variations.lemmatized));
      
      if (isClickable) {
        return (
          <ClickableWord
            key={`${keyPrefix}-${i}`}
            keyId={`${keyPrefix}-${i}`}
            word={word}
            gameColor={gameColor}
            highlightWord={isTargetWord}
            isGrayedOut={isGrayedOut}
            onClick={() => onWordSelection(cleanWord)}
          />
        );
      }
      
      return (
        <span 
          key={`${keyPrefix}-${i}`}
          style={{ 
            fontSize: "1rem",
            color: "#666",
            cursor: "default",
            textDecoration: "none",
            fontWeight: "normal",
            padding: "1px 2px",
            whiteSpace: "nowrap",
          }}
        >
          {word}
        </span>
      );
    });
  };



  // Pre-compute all text to find first occurrence of each word
  const allText = React.useMemo(() => {
    return wordInfo.synsets.map((synset, defIndex) => ({
      defId: `def-${defIndex}`,
      text: synset.definition,
      examples: synset.examples.map((ex, exIndex) => ({
        exId: `ex-${defIndex}-${exIndex}`,
        text: ex
      }))
    }));
  }, [wordInfo.synsets]);

  // Build a map of word -> first occurrence location (textId + wordIndex)
  // Priority: definitions first, then examples (ensures clickable words appear in definitions when possible)
  const firstOccurrenceMap = React.useMemo(() => {
    const map = new Map<string, string>();
    
    // First pass: scan all definitions
    allText.forEach(({ defId, text }) => {
      const words = text.split(/(\W+)/);
      words.forEach((w, wordIndex) => {
        const cleanWord = w.toLowerCase().replace(/[^a-z0-9]/g, '');
        if (cleanWord.length > MIN_WORD_LENGTH && wordInfo.clickableWords.includes(cleanWord) && !map.has(cleanWord)) {
          map.set(cleanWord, `${defId}-${wordIndex}`);
        }
      });
    });
    
    // Second pass: scan all examples (only if word not already found in definitions)
    allText.forEach(({ examples }) => {
      examples.forEach(({ exId, text: exText }) => {
        const exWords = exText.split(/(\W+)/);
        exWords.forEach((w, wordIndex) => {
          const cleanWord = w.toLowerCase().replace(/[^a-z0-9]/g, '');
          if (cleanWord.length > MIN_WORD_LENGTH && wordInfo.clickableWords.includes(cleanWord) && !map.has(cleanWord)) {
            map.set(cleanWord, `${exId}-${wordIndex}`);
          }
        });
      });
    });
    
    return map;
  }, [allText, wordInfo.clickableWords]);

  // Render text with clickable words (only first occurrence of each word is clickable)
  const renderTextWithClickableWords = (text: string, textId: string = '') => {
    const words = text.split(/(\W+)/);
    return words.map((w, i) => { 
      const cleanWord = w.toLowerCase().replace(/[^a-z0-9]/g, '');
      // If makeWordsUnclickable is true, nothing is clickable
      // Check if this specific occurrence (textId + word index) is the first occurrence
      const isClickable = !makeWordsUnclickable && 
                          cleanWord.length > MIN_WORD_LENGTH && 
                          wordInfo.clickableWords.includes(cleanWord) &&
                          firstOccurrenceMap.get(cleanWord) === `${textId}-${i}`;

      if (isClickable) {
        // Check if this word would lead to the target/goal word
        const targetGameWord = wordInfo.wordLemmaMap[cleanWord];
        const goalWordLower = gameState?.goalWord.toLowerCase();
        const isTargetWord = gameState && targetGameWord && (
          targetGameWord.variations.display.toLowerCase() === goalWordLower || 
          targetGameWord.variations.lemmatized.toLowerCase() === goalWordLower
        );
        
        // Check if this word is grayed out
        const isGrayedOut = grayedOutWords.has(cleanWord) || 
          (targetGameWord && grayedOutWords.has(targetGameWord.variations.lemmatized));

        return (
          <ClickableWord
            key={`${textId}-${i}`}
            keyId={`${textId}-${i}`}
            word={w}
            gameColor={gameColor}
            highlightWord={isTargetWord}
            isGrayedOut={isGrayedOut}
            onClick={() => onWordSelection(cleanWord)}
          />
        );
      }
      return <span key={`${textId}-${i}`} style={{ fontWeight: "normal" }}>{w}</span>;
    });
  };

  return (
    <div className="dictionary-entry" style={{ fontFamily: game.fontSecondary }}>
      {/* Render definitions */}
      <div style={{ 
        fontSize: "1.1rem", 
        color: "#2c2c2c",
        textAlign: "left",
        lineHeight: "1.6",
      }}>
        {wordInfo.synsets.map((synset, defIndex) => (
          <div key={defIndex} style={{ 
            marginBottom: defIndex < wordInfo.synsets.length - 1 ? "2rem" : "0" 
          }}>
            {/* Part of speech and definition */}
            <div style={{ marginBottom: "0.8rem" }}>
              <span style={{fontStyle: "italic", marginRight: "0.5rem"}}>{defIndex + 1}. </span>
              <span style={{ 
                fontStyle: "italic", 
                color: "rgba(0, 0, 0, 0.6)",
                fontSize: "0.95rem",
                marginRight: "0.5rem",
                fontWeight: "600"
              }}>
                {synset.type === "adjective" ? "adj" : synset.type === "adverb" ? "adv" : synset.type}.
              </span>
              <span style={{ fontSize: "1.05rem" }}>
                {renderTextWithClickableWords(synset.definition, `def-${defIndex}`)}
              </span>
            </div>
            
            {/* Examples */}
            {synset.examples && synset.examples.length > 0 && (
              <div style={{ 
                marginLeft: "1.5rem", 
                marginTop: "0.5rem",
                fontStyle: "italic",
                color: "rgba(0, 0, 0, 0.65)",
                fontSize: "0.95rem"
              }}>
                {synset.examples.map((example, exIndex) => (
                  <div key={exIndex} style={{ marginBottom: "0.3rem" }}>
                    "{renderTextWithClickableWords(example, `ex-${defIndex}-${exIndex}`)}"
                  </div>
                ))}
              </div>
            )}

          </div>
        ))}

        {/* Related section at the bottom - exclude words that appear in definitions */}
        {(() => {
          if (!wordInfo.relatedWords || wordInfo.relatedWords.length === 0) {
            return null;
          }

          // Extract all words from definitions to filter them out
          const definitionText = wordInfo.synsets
            .map(synset => synset.definition)
            .join(' ')
            .toLowerCase()
            .replace(/[^a-zA-Z0-9\s]/g, '')
            .split(/\s+/)
            .filter(w => w.length > MIN_WORD_LENGTH);
          
          const definitionWords = new Set(definitionText);

          // Filter out words that appear in definitions
          const filteredRelatedWords = wordInfo.relatedWords.filter(gameWord => 
            !definitionWords.has(gameWord.variations.display.toLowerCase())
          );

          if (filteredRelatedWords.length === 0) {
            return null;
          }

          return (
            <div style={{ 
              marginTop: "2rem",
              borderTop: "1px solid #e5e5e5",
              paddingTop: "1.5rem"
            }}>
              <div style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "baseline",
                gap: "0.25rem"
              }}>
                <span style={{ 
                  fontSize: "0.95rem", 
                  color: "#666", 
                  marginRight: "0.5rem",
                  fontWeight: "600",
                  textTransform: "capitalize",
                  flexShrink: 0
                }}>
                  Related:
                </span>
                <div style={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "baseline",
                  gap: "0.25rem"
                }}>
                  {createWordBlobs(filteredRelatedWords, 'related', true)}
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};
